import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, Clock, Loader2, ArrowRight, Eye, EyeOff, CheckCircle2, RotateCw, Trash2,
  ChevronRight, ChevronDown, Info, Sparkles, X, Code2, Network, MessagesSquare, Rocket, Star, Lock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthModal } from '../../hooks/useAuthModal';
import AuthModal from '../Auth/AuthModal';
import { showToast } from '../../utils/errorHandler';
import {
  listPreScreenTemplates,
  getPreScreenQuota,
  startPreScreen,
  listMyPreScreens,
  deletePreScreen,
  setPreScreenSessionVisibility,
  type PreScreenTemplate,
  type PreScreenQuota,
  type PreScreenMine,
} from '../../api/interview';

/**
 * Stable display order of the standardised Cevee pre-screens. Any family the
 * backend returns that is NOT in this list is appended after these (so a new
 * pre-screen still shows up even before the frontend knows about it).
 */
const FAMILY_ORDER = [
  'cevee_prescreen_intro',
  'cevee_prescreen_behavioral',
  'cevee_prescreen_founders',
  'cevee_prescreen_technical',
  'cevee_prescreen_coding',
];

const FAMILY_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  cevee_prescreen_intro: Sparkles,
  cevee_prescreen_coding: Code2,
  cevee_prescreen_technical: Network,
  cevee_prescreen_behavioral: MessagesSquare,
  cevee_prescreen_founders: Rocket,
};

function fmtScore(score: number | null): string {
  if (score == null) return '-';
  return `${Math.round(score)}%`;
}

function orderFamilies(templates: PreScreenTemplate[]): PreScreenTemplate[] {
  // One template per family (the list endpoint returns DE + EN rows; we pick
  // the language-matched one in `dedupeByFamily`), ordered by FAMILY_ORDER.
  const known = FAMILY_ORDER
    .map((fk) => templates.find((t) => t.family_key === fk))
    .filter((t): t is PreScreenTemplate => !!t);
  const extra = templates.filter((t) => !FAMILY_ORDER.includes(t.family_key));
  return [...known, ...extra];
}

/**
 * Explorer-facing Passport Pre-Screen.
 *
 * Lists every standardised Cevee pre-screen (get-to-know, coding, technical
 * breadth, communication & leadership, founders associate, …). The candidate
 * runs each on themselves, sees their results, and controls per attempt
 * whether recruiters can see them. Quota: 3 attempts per family / 90 days.
 */
const ExplorerPreScreen: React.FC = () => {
  const { t, i18n } = useTranslation(['passport', 'profile']);
  const { user, apiCall } = useAuth();
  const { showAuthModal, setShowAuthModal, requireAuth } = useAuthModal();
  const navigate = useNavigate();
  const isSignedIn = !!user?.userId;
  const lang = (i18n.language || 'de').slice(0, 2);

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<PreScreenTemplate[]>([]);
  const [quotaByFamily, setQuotaByFamily] = useState<Record<string, PreScreenQuota | null>>({});
  const [mine, setMine] = useState<PreScreenMine[]>([]);
  const [consentFamily, setConsentFamily] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [startingFamily, setStartingFamily] = useState<string | null>(null);
  const [savingVisId, setSavingVisId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [showSkillChecks, setShowSkillChecks] = useState(false);
  const [infoFamily, setInfoFamily] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.userId) {
      setTemplates([]);
      setQuotaByFamily({});
      setMine([]);
      setShowRules(false);
      setLoading(false);
      return;
    }
    try {
      const [tplRes, mineRes] = await Promise.all([
        listPreScreenTemplates(apiCall, lang).catch(() => ({ templates: [] })),
        listMyPreScreens(apiCall).catch(() => ({ prescreens: [] })),
      ]);

      // The endpoint returns DE + EN rows per family; keep the lang match
      // (fallback to the first row of that family).
      const byFamily = new Map<string, PreScreenTemplate>();
      for (const tpl of tplRes.templates) {
        const existing = byFamily.get(tpl.family_key);
        if (!existing || (tpl.language === lang && existing.language !== lang)) {
          byFamily.set(tpl.family_key, tpl);
        }
      }
      const ordered = orderFamilies(Array.from(byFamily.values()));
      setTemplates(ordered);
      setMine(mineRes.prescreens || []);

      const quotas = await Promise.all(
        ordered.map((tpl) =>
          getPreScreenQuota(apiCall, tpl.family_key)
            .then((r) => [tpl.family_key, r.quota] as const)
            .catch(() => [tpl.family_key, null] as const),
        ),
      );
      setQuotaByFamily(Object.fromEntries(quotas));
    } catch {
      /* fail-soft: render the empty/start state */
    } finally {
      setLoading(false);
    }
  }, [user?.userId, apiCall, lang]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh quota + results when returning from the interview room so the
  // counters reflect the attempt that just started.
  useEffect(() => {
    const refresh = () => { void load(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const completedByFamily = useMemo(() => {
    const map: Record<string, PreScreenMine[]> = {};
    for (const m of mine) {
      if (m.status !== 'completed' || !m.family_key) continue;
      (map[m.family_key] ||= []).push(m);
    }
    return map;
  }, [mine]);

  const familyMeta = useCallback(
    (tpl: PreScreenTemplate) => {
      const fk = tpl.family_key;
      const Icon = FAMILY_ICON[fk] || Sparkles;
      const title = t(`prescreen.families.${fk}.title`, { defaultValue: tpl.title });
      const typeLabel = t(`prescreen.families.${fk}.type`, {
        defaultValue: tpl.type || t('prescreen.cardType', 'Intro'),
      });
      const body = t(`prescreen.families.${fk}.body`, {
        defaultValue: tpl.description || '',
      });
      const levelLabel = fk === 'cevee_prescreen_intro'
        ? t('prescreen.levelLabel.all', { defaultValue: 'All' })
        : tpl.difficulty
          ? t(`prescreen.levelLabel.${tpl.difficulty}`, { defaultValue: tpl.difficulty })
          : null;
      const rawCriteria = t(`prescreen.families.${fk}.criteria`, { returnObjects: true });
      const criteria = Array.isArray(rawCriteria)
        ? (rawCriteria as unknown[]).filter((c): c is string => typeof c === 'string')
        : [];
      const criteriaLabel = t(`prescreen.families.${fk}.criteriaLabel`, {
        defaultValue: t('prescreen.criteriaLabel', 'What we look at'),
      });
      const roles = t(`prescreen.families.${fk}.roles`, { defaultValue: '' });
      return { Icon, title, typeLabel, body, levelLabel, criteria, criteriaLabel, roles };
    },
    [t],
  );

  const runStart = async (family: string) => {
    try {
      setStartingFamily(family);
      const res = await startPreScreen(apiCall, {
        family_key: family,
        language: lang,
        explicit_consent: true,
      });
      setConsentFamily(null);
      const token = res.invite_url.split('/').pop() || res.raw_token;
      navigate(`/interview/invite/${token}`);
    } catch (e: any) {
      const msg = e?.message || '';
      if (msg.includes('quota')) {
        showToast(t('passport:prescreen.quotaExhausted', 'No attempts left for this period.'), 'error');
      } else {
        showToast(t('passport:prescreen.startError', 'Could not start the Intro. Please try again.'), 'error');
      }
      setStartingFamily(null);
    }
  };

  const onStartClick = (family: string) => {
    if (!requireAuth()) return;
    const quota = quotaByFamily[family];
    const remaining = quota?.remaining ?? 0;
    const isAdmin = !!quota?.is_admin;
    if (!isAdmin && remaining <= 0) {
      showToast(t('passport:prescreen.quotaExhausted', 'No attempts left for this period.'), 'info');
      return;
    }
    setConsentChecked(false);
    setConsentFamily(family);
  };

  const onConsentConfirm = () => {
    if (!consentChecked) {
      showToast(t('passport:prescreen.consentRequired', 'Please confirm to continue.'), 'info');
      return;
    }
    if (consentFamily) void runStart(consentFamily);
  };

  const toggleAttemptVisibility = async (sessionId: string, currentlyVisible: boolean) => {
    if (savingVisId) return;
    const nextVisible = !currentlyVisible;
    setMine((prev) =>
      prev.map((m) => (m.session_id === sessionId ? { ...m, recruiter_visible: nextVisible } : m)),
    );
    try {
      setSavingVisId(sessionId);
      await setPreScreenSessionVisibility(apiCall, sessionId, nextVisible);
      showToast(t('passport:prescreen.visibilitySaved', 'Visibility updated.'), 'success');
    } catch {
      setMine((prev) =>
        prev.map((m) => (m.session_id === sessionId ? { ...m, recruiter_visible: currentlyVisible } : m)),
      );
      showToast(t('profile:contactInfo.error', 'Failed to update. Please try again.'), 'error');
    } finally {
      setSavingVisId(null);
    }
  };

  const onDelete = async (sessionId: string) => {
    if (deletingId) return;
    if (!window.confirm(t('passport:prescreen.deleteConfirm', 'Delete this Intro result permanently? This cannot be undone.'))) {
      return;
    }
    try {
      setDeletingId(sessionId);
      await deletePreScreen(apiCall, sessionId);
      setMine((prev) => prev.filter((m) => m.session_id !== sessionId));
      showToast(t('passport:prescreen.deleteSuccess', 'Intro deleted.'), 'success');
    } catch {
      showToast(t('passport:prescreen.deleteError', 'Could not delete. Please try again.'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const renderCard = (tpl: PreScreenTemplate): React.ReactNode => {
    const fk = tpl.family_key;
    const { Icon, title, typeLabel, body, levelLabel, criteria, criteriaLabel, roles } = familyMeta(tpl);
    const quota = quotaByFamily[fk];
    const isAdmin = !!quota?.is_admin;
    const used = quota?.used ?? 0;
    const remaining = quota?.remaining ?? 0;
    const total = quota?.total ?? 3;
    const durationMin = tpl.duration_minutes || 15;
    const completed = completedByFamily[fk] || [];
    const hasResult = completed.length > 0;
    const starting = startingFamily === fk;
    const canStart = isAdmin || remaining > 0;

    return (
      <div
        key={fk}
        className="group rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition"
      >
        <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
          <span className="inline-flex items-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-pink-700 bg-pink-50 border border-pink-100 px-1.5 py-0.5 rounded-full">
            {t('prescreen.cardBadge', 'Passport')}
          </span>
          <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded-full">
            <Icon className="w-2.5 h-2.5" />
            {typeLabel}
          </span>
          {levelLabel && (
            <span className="inline-flex items-center text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-full">
              {levelLabel}
            </span>
          )}
        </div>

        <div className="mt-1.5 flex items-start gap-1.5">
          <h4 className="text-[15px] sm:text-base font-semibold text-slate-900 leading-snug">
            {title}
          </h4>
          {roles && (
            <button
              type="button"
              onClick={() => setInfoFamily((f) => (f === fk ? null : fk))}
              aria-expanded={infoFamily === fk}
              className={[
                'flex-shrink-0 mt-0.5 p-0.5 rounded-full transition-colors',
                infoFamily === fk
                  ? 'text-pink-600 bg-pink-50'
                  : 'text-slate-400 hover:text-pink-600 hover:bg-pink-50',
              ].join(' ')}
              title={t('prescreen.rolesTooltip', 'For which roles?')}
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {body && (
          <p className="mt-1.5 text-[13px] sm:text-sm text-slate-600 leading-relaxed">{body}</p>
        )}

        {roles && infoFamily === fk && (
          <div className="mt-2 rounded-2xl bg-pink-50/60 border border-pink-100 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-pink-700">
              {t('prescreen.rolesLabel', 'Good for')}
            </p>
            <p className="mt-0.5 text-[12px] text-slate-600 leading-relaxed">{roles}</p>
          </div>
        )}

        {criteria.length > 0 && (
          <div className="mt-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {criteriaLabel}
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {criteria.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center text-[10px] sm:text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded-full"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {isSignedIn && quota && !isAdmin && remaining <= 0 && (
          <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
            {t('prescreen.quotaExhaustedBody', "You've used all {{total}} attempts for 90 days.", { total })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 min-w-0">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t('prescreen.durationApprox', '~{{min}} min', { min: durationMin })}
            </span>
            {isSignedIn && quota && (
              isAdmin ? (
                <span className="text-pink-700 font-semibold">
                  {t('prescreen.attemptsAdmin', 'Unlimited attempts (admin)')}
                </span>
              ) : remaining > 0 ? (
                <span className="text-pink-700 font-semibold">
                  {t('prescreen.attemptsLeft', '{{remaining}} of {{total}} attempts left', { remaining, total })}
                </span>
              ) : (
                <span className="text-slate-500 font-medium">
                  {t('prescreen.attemptsUsed', '{{used}} of {{total}} attempts used', { used, total })}
                </span>
              )
            )}
          </div>

          <button
            type="button"
            onClick={() => onStartClick(fk)}
            disabled={isSignedIn && !canStart}
            className={
              'inline-flex flex-shrink-0 items-center gap-1.5 text-[13px] sm:text-sm font-semibold px-3 sm:px-4 py-1.5 rounded-full transition ' +
              (starting
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : !isSignedIn || canStart
                  ? 'bg-slate-900 text-white hover:bg-pink-600'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed')
            }
          >
            {starting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : !isSignedIn ? (
              <ArrowRight className="w-3.5 h-3.5" />
            ) : hasResult ? (
              <RotateCw className="w-3.5 h-3.5" />
            ) : (
              <ArrowRight className="w-3.5 h-3.5" />
            )}
            {!isSignedIn
              ? t('prescreen.loggedOutCta', 'Sign in')
              : !canStart
                ? t('prescreen.noAttempts', 'No attempts left this period')
                : hasResult
                  ? t('prescreen.retake', 'Retake Intro')
                  : t('prescreen.start', 'Start Intro')}
          </button>
        </div>

        {/* Per-family results + visibility */}
        {isSignedIn && hasResult && (
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
            <span className="text-[11px] sm:text-xs font-semibold text-slate-700">
              {t('prescreen.yourResults', 'Your results')}
            </span>
            {completed.map((r) => {
              const isVisible = !!r.recruiter_visible;
              return (
                <div
                  key={r.session_id}
                  className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 gap-2"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/mock/sessions/${r.session_id}`)}
                    title={t('prescreen.viewDetails', 'View details')}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-[11px] sm:text-xs text-slate-600 truncate">
                      {r.completed_at
                        ? new Date(r.completed_at * 1000).toLocaleDateString()
                        : t('prescreen.completed', 'Completed')}
                    </span>
                    {/* Score hidden from UI */}
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => toggleAttemptVisibility(r.session_id, isVisible)}
                      disabled={savingVisId === r.session_id}
                      title={
                        isVisible
                          ? t('prescreen.visibleToRecruiters', 'Visible to recruiters')
                          : t('prescreen.hiddenFromRecruiters', 'Hidden from recruiters')
                      }
                      className={`flex items-center gap-1 text-[10px] sm:text-[11px] px-2 py-1 rounded-lg transition-colors ${
                        isVisible
                          ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {savingVisId === r.session_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : isVisible ? (
                        <Eye className="w-3.5 h-3.5" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(r.session_id)}
                      disabled={deletingId === r.session_id}
                      title={t('prescreen.delete', 'Delete')}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {deletingId === r.session_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const introTpl = templates.find((tpl) => tpl.family_key === 'cevee_prescreen_intro');
  const skillTemplates = templates.filter((tpl) => tpl.family_key !== 'cevee_prescreen_intro');
  const skillResultCount = skillTemplates.reduce(
    (acc, tpl) => acc + (completedByFamily[tpl.family_key]?.length ? 1 : 0),
    0,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> …
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4">
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
        <Lock className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] sm:text-xs text-amber-800 leading-relaxed">
          {t('prescreen.visibilityBadge', 'You control what recruiters see - only attempts you share are visible.')}
        </p>
      </div>

      {/* Header */}
      <div>
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm sm:text-base font-semibold text-slate-800">{t('prescreen.title')}</h3>
          {isSignedIn && (
            <button
              type="button"
              onClick={() => setShowRules((v) => !v)}
              aria-expanded={showRules}
              className={
                'inline-flex flex-shrink-0 items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full transition ' +
                (showRules ? 'text-slate-800 bg-slate-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
              }
            >
              <Info className="w-3 h-3" />
              {t('prescreen.rulesTitle', 'How it works')}
            </button>
          )}
        </div>
        <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 leading-relaxed">
          {t('prescreen.explorerLead', 'Do it once, get found by multiple companies, and skip first-round interviews.')}
        </p>
      </div>

      {isSignedIn && showRules && (
        <div className="rounded-2xl bg-slate-50 border border-slate-100 px-3 py-3 space-y-2">
          <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">
            {t('prescreen.explorerSubtitle', 'A shared Cevee Intro makes you stand out from ordinary applications - recruiters discover you directly through it. You decide, per attempt, whether it\'s visible.')}
          </p>
          <ul className="space-y-1 text-[11px] text-slate-600 leading-relaxed list-disc pl-4">
            <li>{t('prescreen.rules1', 'You get 3 attempts per Intro type within 90 days.')}</li>
            <li>{t('prescreen.rules2', 'You choose per attempt whether recruiters can see it.')}</li>
            <li>{t('prescreen.rules3', 'You can delete results anytime.')}</li>
          </ul>
        </div>
      )}

      {!isSignedIn && (
        <p className="text-[12px] text-slate-600 leading-relaxed">
          {t('prescreen.loggedOutHint', 'Sign in to run your Intro and choose what recruiters can see.')}
        </p>
      )}

      {/* Intro is always front and center */}
      {introTpl && renderCard(introTpl)}

      {/* Role-specific skill checks collapse into one clean section */}
      {skillTemplates.length > 0 && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setShowSkillChecks((v) => !v)}
            aria-expanded={showSkillChecks}
            className="w-full flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 text-left shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-amber-50 border border-amber-200 text-amber-500 flex-shrink-0">
                <Star className="w-4 h-4 fill-amber-400" />
              </span>
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-slate-900 leading-tight">
                  {t('prescreen.skillChecksTitle', 'Cevee Badges')}
                </p>
                <p className="text-[11px] text-slate-500 leading-snug truncate">
                  {t('prescreen.skillChecksSubtitle', 'Optional, role-specific checks - coding, technical, behavioral & more.')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {skillResultCount > 0 && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                  title={t('prescreen.yourResults', 'Your results')}
                />
              )}
              <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                {skillTemplates.length}
              </span>
              <ChevronDown
                className={
                  'w-4 h-4 text-slate-400 transition-transform ' + (showSkillChecks ? 'rotate-180' : '')
                }
              />
            </div>
          </button>

          {showSkillChecks && skillTemplates.map((tpl) => renderCard(tpl))}
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} signupPath="/signup" />

      {consentFamily && (
        <div
          className="fixed inset-0 z-[10010] flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3 sm:p-4"
          onClick={() => !startingFamily && setConsentFamily(null)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="prescreen-consent-title"
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-5 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-pink-50 border border-pink-100 text-pink-600 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h2 id="prescreen-consent-title" className="text-sm font-semibold text-slate-900">
                    {t('prescreen.consentTitle', 'Start Intro')}
                  </h2>
                  <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                    {t('prescreen.consentLead', 'A short AI conversation with recording, private until you choose to share.')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !startingFamily && setConsentFamily(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-pink-600 focus:ring-pink-500"
              />
              <span className="text-sm text-slate-800 leading-relaxed">
                {t('prescreen.consent', 'I agree to the recording and AI evaluation of this conversation.')}
              </span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConsentFamily(null)}
                disabled={!!startingFamily}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                {t('prescreen.consentCancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={onConsentConfirm}
                disabled={!!startingFamily || !consentChecked}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-slate-900 hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
              >
                {startingFamily ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {t('prescreen.consentConfirm', 'Start')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorerPreScreen;
